import { API_BASE } from "../config";
import React, { useState, useEffect } from 'react';

const ExerciseLibraryBrowser = ({ fetchWithAuth }) => {
  // State for exercises and pagination
  const [exercises, setExercises] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(24); // Show 24 exercises per page (4x6 grid)

  // State for filters
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedMuscle, setSelectedMuscle] = useState('');

  // State for filter options (loaded from API)
  const [categories, setCategories] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);

  // State for video modal
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedExercise, setSelectedExercise] = useState(null);

  // State for exercise detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [exerciseDetail, setExerciseDetail] = useState(null);

  // Load filter options on mount
  useEffect(() => {
    loadCategories();
    loadMuscleGroups();
  }, []);

  // Load exercises when filters or pagination change
  useEffect(() => {
    loadExercises();
  }, [offset, selectedCategory, selectedDifficulty, selectedMuscle]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setOffset(0); // Reset to first page on search
      loadExercises();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText]);

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/exercise-library/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const loadMuscleGroups = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/exercise-library/muscle-groups`);
      if (response.ok) {
        const data = await response.json();
        setMuscleGroups(data.muscle_groups || []);
      }
    } catch (error) {
      console.error('Failed to load muscle groups:', error);
    }
  };

  const loadExercises = async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('limit', limit);
      params.append('offset', offset);
      params.append('has_video', 'true'); // Only show exercises with videos
      
      if (searchText) params.append('search', searchText);
      if (selectedCategory) params.append('category', selectedCategory);
      if (selectedDifficulty) params.append('difficulty_level', selectedDifficulty);
      
      let url = `${API_BASE}/api/exercise-library/?${params.toString()}`;
      
      // If muscle filter is set, use the muscle-specific endpoint
      if (selectedMuscle) {
        url = `${API_BASE}/api/exercise-library/by-muscle/${encodeURIComponent(selectedMuscle)}?limit=${limit}`;
      }

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setExercises(data.exercises || []);
        setTotalCount(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load exercises:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExerciseDetail = async (exerciseId) => {
    try {
      const response = await fetch(`${API_BASE}/api/exercise-library/${exerciseId}`);
      if (response.ok) {
        const data = await response.json();
        setExerciseDetail(data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error('Failed to load exercise details:', error);
    }
  };

  const handleWatchVideo = (exercise) => {
    setSelectedExercise(exercise);
    setShowVideoModal(true);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setSelectedCategory('');
    setSelectedDifficulty('');
    setSelectedMuscle('');
    setOffset(0);
  };

  const totalPages = Math.ceil(totalCount / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const getDifficultyColor = (level) => {
    switch (level) {
      case 'beginner': return { bg: '#D1FAE5', text: '#065F46' };
      case 'intermediate': return { bg: '#FEF3C7', text: '#92400E' };
      case 'advanced': return { bg: '#FEE2E2', text: '#991B1B' };
      default: return { bg: '#F3F4F6', text: '#4B5563' };
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Dumbbell': '',
      'Barbell': '',
      'Bodyweight': '',
      'Cable': '',
      'Machine': '',
      'Kettlebell': '',
      'Resistance Band': '',
      'Yoga': '',
      'Stretching': '',
      'Medicine Ball': '',
      'TRX': '',
      'Landmine': '',
      'Smith Machine': '',
      'Bench': '',
      'Pull-up Bar': '',
      'Cardio': '',
      'Stability Ball': '',
      'Foam Roller': ''
    };
    return icons[category] || '';
  };

  return (
    <div className="elb-container">
      {/* Search and Filter Bar */}
      <div className="elb-filterBar">
        <div className="elb-filterControls">
          {/* Search Input */}
          <div>
            <label className="elb-searchLabel">
              Search Exercises
            </label>
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by name or muscle..."
              className="elb-searchInput"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="elb-equipmentLabel">
              Equipment
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => { setSelectedCategory(e.target.value); setOffset(0); }}
              className="elb-equipmentSelect"
            >
              <option value="">All Equipment</option>
              {categories.map(cat => (
                <option key={cat.category} value={cat.category}>
                  {cat.category} ({cat.count})
                </option>
              ))}
            </select>
          </div>

          {/* Difficulty Filter */}
          <div>
            <label className="elb-difficultyLabel">
              Difficulty
            </label>
            <select
              value={selectedDifficulty}
              onChange={(e) => { setSelectedDifficulty(e.target.value); setOffset(0); }}
              className="elb-difficultySelect"
            >
              <option value="">All Levels</option>
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>

          {/* Muscle Group Filter */}
          <div>
            <label className="elb-muscleLabel">
              Muscle Group
            </label>
            <select
              value={selectedMuscle}
              onChange={(e) => { setSelectedMuscle(e.target.value); setOffset(0); }}
              className="elb-muscleSelect"
            >
              <option value="">All Muscles</option>
              {muscleGroups.map(muscle => (
                <option key={muscle} value={muscle}>
                  {muscle.charAt(0).toUpperCase() + muscle.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters Button */}
          <button
            onClick={handleClearFilters}
            className="elb-clearButton"
          >
            Clear
          </button>
        </div>

        {/* Results Count */}
        <div className="elb-resultsCount">
          {loading ? 'Loading...' : `Found ${totalCount.toLocaleString()} exercises`}
          {(selectedCategory || selectedDifficulty || selectedMuscle || searchText) && (
            <span className="elb-filteredTag">
              (filtered)
            </span>
          )}
        </div>
      </div>

      {/* Exercise Grid */}
      {loading ? (
        <div className="elb-loadingContainer">
          <div className="elb-loadingSpinner"></div>
          Loading exercises...
        </div>
      ) : exercises.length === 0 ? (
        <div className="elb-noResultsContainer">
          <div className="elb-noResultsIcon"></div>
          No exercises found. Try adjusting your filters.
        </div>
      ) : (
        <div className="elb-grid">
          {exercises.map(exercise => {
            const diffColors = getDifficultyColor(exercise.difficulty_level);
            return (
              <div
                key={exercise.id}
                className="elb-exerciseCard"
                onClick={() => loadExerciseDetail(exercise.id)}
              >
                {/* Card Header */}
                <div className="elb-cardHeader">
                  <div className="elb-cardHeaderTop">
                    <span className="elb-categoryIcon">
                      {getCategoryIcon(exercise.category)}
                    </span>
                    {exercise.difficulty_level && (
                      <span style={{
                        padding: '4px 8px',
                        backgroundColor: diffColors.bg,
                        color: diffColors.text,
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {exercise.difficulty_level}
                      </span>
                    )}
                  </div>
                  <h4 className="elb-exerciseName">
                    {exercise.exercise_name}
                  </h4>
                  <p className="elb-equipmentCategory">
                    {exercise.category}
                  </p>
                </div>

                {/* Card Body */}
                <div className="elb-cardBody">
                  {exercise.primary_muscles_raw && (
                    <p className="elb-muscleText">
                      <strong>Muscles:</strong> {exercise.primary_muscles_raw}
                    </p>
                  )}
                  {exercise.video_gender && (
                    <p className="elb-demoInfo">
                      Demo: {exercise.video_gender}
                    </p>
                  )}

                  {/* Watch Video Button - only show if video exists */}
                  {exercise.video_url && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWatchVideo(exercise);
                      }}
                      className="elb-watchVideoButton"
                    >
                      <span>▶</span> Watch Video
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="elb-pagination">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="elb-prevButton" style={{ backgroundColor: offset === 0 ? '#E5E7EB' : 'var(--brand-primary)', color: offset === 0 ? '#9CA3AF' : 'white', cursor: offset === 0 ? 'not-allowed' : 'pointer' }}
          >
            ← Previous
          </button>
          
          <span className="elb-pageIndicator">
            Page {currentPage} of {totalPages}
          </span>
          
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={currentPage >= totalPages}
            className="elb-nextButton" style={{ backgroundColor: currentPage >= totalPages ? '#E5E7EB' : 'var(--brand-primary)', color: currentPage >= totalPages ? '#9CA3AF' : 'white', cursor: currentPage >= totalPages ? 'not-allowed' : 'pointer' }}
          >
            Next →
          </button>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && selectedExercise && (
        <div
          className="elb-videoModalOverlay"
          onClick={() => setShowVideoModal(false)}
        >
          <div
            className="elb-videoModalContent"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="elb-videoModalHeader">
              <div>
                <h3 className="elb-videoModalTitle">
                  {selectedExercise.exercise_name}
                </h3>
                <p className="elb-videoModalSubtitle">
                  {selectedExercise.category} • {selectedExercise.primary_muscles_raw || 'Multiple muscles'}
                </p>
              </div>
              <button
                onClick={() => setShowVideoModal(false)}
                className="elb-videoModalCloseButton"
              >
                ×
              </button>
            </div>

            {/* Video Player */}
            <div className="elb-videoPlayerContainer">
              <video
                src={selectedExercise.video_url}
                controls
                autoPlay
                className="elb-videoElement"
              />
            </div>

            {/* Tips Section (if available) */}
            {selectedExercise.tips && (
              <div className="elb-videoTipsSection">
                <h4 className="elb-videoTipsHeading">
                  Tips
                </h4>
                <p className="elb-videoTipsText">
                  {selectedExercise.tips}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Exercise Detail Modal */}
      {showDetailModal && exerciseDetail && (
        <div
          className="elb-detailModalOverlay"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="elb-detailModalContent"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="elb-detailModalHeader">
              <div>
                <div className="elb-detailHeaderTitle">
                  <span className="elb-detailIcon">
                    {getCategoryIcon(exerciseDetail.category)}
                  </span>
                  <h2 className="elb-detailExerciseName">
                    {exerciseDetail.exercise_name}
                  </h2>
                </div>
                <div className="elb-detailHeaderMeta">
                  <span className="elb-categoryTag">
                    {exerciseDetail.category}
                  </span>
                  {exerciseDetail.difficulty_level && (
                    <span style={{
                      padding: '4px 10px',
                      backgroundColor: getDifficultyColor(exerciseDetail.difficulty_level).bg,
                      color: getDifficultyColor(exerciseDetail.difficulty_level).text,
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}>
                      {exerciseDetail.difficulty_level}
                    </span>
                  )}
                  {exerciseDetail.exercise_type && (
                    <span className="elb-exerciseTypeTag">
                      {exerciseDetail.exercise_type}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="elb-detailCloseButton"
              >
                ×
              </button>
            </div>

            {/* Content */}
            <div className="elb-detailContent">
              {/* Muscles */}
              <div className="elb-musclesSection">
                <h4 className="elb-musclesHeading">
                  Target Muscles
                </h4>
                {exerciseDetail.primary_muscles_raw && (
                  <p className="elb-primaryMuscles">
                    <strong>Primary:</strong> {exerciseDetail.primary_muscles_raw}
                  </p>
                )}
                {exerciseDetail.secondary_muscles_raw && (
                  <p className="elb-secondaryMuscles">
                    <strong>Secondary:</strong> {exerciseDetail.secondary_muscles_raw}
                  </p>
                )}
              </div>

              {/* Movement Pattern */}
              {exerciseDetail.movement_pattern && (
                <div className="elb-movementSection">
                  <h4 className="elb-movementHeading">
                    Movement Pattern
                  </h4>
                  <p className="elb-movementText">
                    {exerciseDetail.movement_pattern}
                  </p>
                </div>
              )}

              {/* Instructions */}
              {exerciseDetail.instructions && (
                <div className="elb-instructionsSection">
                  <h4 className="elb-instructionsHeading">
                    Instructions
                  </h4>
                  <p className="elb-instructionsText">
                    {exerciseDetail.instructions}
                  </p>
                </div>
              )}

              {/* Form Cues */}
              {exerciseDetail.form_cues && exerciseDetail.form_cues.length > 0 && (
                <div className="elb-formCuesSection">
                  <h4 className="elb-formCuesHeading">
                    Form Cues
                  </h4>
                  <ul className="elb-formCuesList">
                    {exerciseDetail.form_cues.map((cue, i) => (
                      <li key={i} className="elb-formCueItem">{cue}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Tips */}
              {exerciseDetail.tips && (
                <div className="elb-tipsSection">
                  <h4 className="elb-tipsHeading">
                    Tips
                  </h4>
                  <p className="elb-tipsText">
                    {exerciseDetail.tips}
                  </p>
                </div>
              )}

              {/* Calories Info */}
              {exerciseDetail.estimated_calories_per_minute && (
                <div className="elb-caloriesSection">
                  <p className="elb-caloriesText">
                    Est. {exerciseDetail.estimated_calories_per_minute.toFixed(1)} calories/minute
                    {exerciseDetail.met_value && ` • MET: ${exerciseDetail.met_value.toFixed(1)}`}
                  </p>
                </div>
              )}

              {/* Watch Video Button */}
              {exerciseDetail.video_url && (
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    handleWatchVideo(exerciseDetail);
                  }}
                  className="elb-watchInstructionalButton"
                >
                  <span>▶</span> Watch Instructional Video
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExerciseLibraryBrowser;